// Copyright (c) Docugami, Inc. All rights reserved.
import React from 'react';
import { primaryTheme, aaCardSeparatorColor, aaTextColor, emptyColor, homeGray } from './AppStyles';
import { primaryFontExpanded } from 'components/common/Fonts';
import { useSelector } from 'react-redux';
import { AppState } from '../../../store';
import { IContextualMenuItem, IPanelStyles, Panel } from "@fluentui/react";
import { useDgIcons, dgIconNames } from "../../../hooks/dgIcons";
import { Dispatch } from 'redux';
import { setSettingsPanelShowing } from '../../../store/settings/actions';
import { ReactComponent as ExternalLogo } from '../../../assets/ExternalLink.svg';
import { DgHorizontalSpacer, DgSelectable } from '../../common/DgCommonStyledComponents';
import styled from 'styled-components';
import { dgFontFamily, UserTitle } from '../../common/Fonts';
import { Telemetry } from 'telemetry/TelemetryManager';
import { MainMenuEntryClickedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { useOrgSwitcher, IOrgSwitcher } from 'hooks/OrgSwitcher';
import { DeepPartial } from '@reduxjs/toolkit';
import { useDgAuth } from 'hooks/DgAuthentication';

export interface MainMenuProps {
    offestTop: string;
    open: boolean;
    closeCallback: () => void;
    menuItems: MenuItem[];
}

export interface MenuItem {
    title: string;
    icon: JSX.Element;
    listItems: IContextualMenuItem[];
    onClick?: () => void;
}

export function showSettingsMenuItem(dispatch: Dispatch<any>, disabled?: boolean): IContextualMenuItem {
    function enableSettings() {
        dispatch(setSettingsPanelShowing(true));
    }
    return {
        key: 'settings',
        text: 'Settings',
        disabled: disabled,
        onClick: () => { enableSettings() }
    };
}

export function showSwitchOrgMenuItem(switchOrg: () => void, disabled?: boolean): IContextualMenuItem {
    return {
        key: 'switch_workspace',
        text: 'Switch Workspace',
        disabled: disabled,
        onClick: () => switchOrg()
    };
}

const textColor = primaryTheme.palette.black;

const MenuEntry = styled.div<DgSelectable>`
    color: ${props => props.selected ? textColor : aaTextColor};
    width: 100%;
    height: 31px;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-left: 31px;
    font-family: ${dgFontFamily};
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    cursor: ${props => props.selected ? 'pointer' : 'default'};

    &:hover {
        background-color: ${props => props.selected ? primaryTheme.palette.neutralLight : 'none'};
    }
`;

const MenuDivider = styled.div`
    width: 290px;
    height: 0;
    border-width: 1px;
    border-style: solid;
    border-color: ${aaCardSeparatorColor};
`;

const MenuTitleEntry = styled.span<DgSelectable>`
    display: flex;
    align-items: center;
    width: 100%;
    height: 31px;
    padding-left: 5px;
    cursor: ${props => props.selected ? 'pointer' : 'default'};

    &:hover {
        background-color: ${props => props.selected ? 'rgb(237, 235, 233)' : 'none'};
    }
`;

const TitleText = styled(UserTitle)`
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;
`;

const SmallText = styled.div`
    font-family: ${primaryFontExpanded};
    font-size: 13px;
    line-height: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 5px;
    // width: 100%;
`;

const UsernameText = styled(SmallText)`
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;
    padding-bottom: 5px;
    color: ${textColor};
`;

const CenteredUserTitle = styled(UserTitle)`
    display: flex;
    flex-direction: column;
    text-align: center;
    border-bottom: 1px solid ${emptyColor};
    font-size: 26px;
    padding-bottom: 20px;
    color: ${homeGray};
`;

export const MainMenu = (props: MainMenuProps): JSX.Element | null => {
    const dgIcons = useDgIcons();
    const { user } = useDgAuth();
    const orgSwitcher = useOrgSwitcher();

    function invokeEntry(entry: any): void {
        if (!entry.disabled) {
            if (entry.onClick) {
                entry.onClick();
                props.closeCallback();
            } else if (entry.url) {
                window.open(entry.url, '_blank', 'noopener,noreferrer');
                props.closeCallback();
            }
        }
    }

    function UserDetailsUx(): JSX.Element {
        const profileIcon = dgIcons.icon(
            dgIconNames.UserProfileIcon,
            '',
            51,
            51,
            textColor,
            '');

        return <div key="user_details" style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ paddingRight: '17px' }}>{profileIcon}</div>
            <div style={{ flex: 1, width: '222px' }}>
                {userInfo()}
            </div>
        </div>;
    }

    function userInfo(): JSX.Element {
        const orgSwitcherProps = {
            ...orgSwitcher,
            switchOrg: () => { orgSwitcher.switchOrg(); props.closeCallback() }
        }

        const username = user?.name?.toUpperCase() ?? '';
        return (
            <>
                <UsernameText title={username}>{username}</UsernameText>
                <OrganizationNameSwitcher orgSwitcherProps={orgSwitcherProps} />
            </>
        );
    }


    function menuEntryUx(entry: IContextualMenuItem): JSX.Element {
        const logoStyle = {
            width: '20px',
            height: '20px',
            marginLeft: 'auto',
            marginRight: 0
        }

        let disabled = entry.disabled || !(entry.onClick || entry.url);

        function onClick() {
            Telemetry.trackEvent<MainMenuEntryClickedSchema>({
                name: entry.text ?? '',
                eventName: TelemetryEvent.MainMenuEntryClicked
            });
            invokeEntry(entry);
        }

        return (
            <div key={entry.key} style={{ display: 'flex', flexDirection: 'row' }}>
                <DgHorizontalSpacer key="hs" width={"23px"} />
                <MenuEntry id={entry.key} key={entry.key} selected={!disabled} onClick={() => onClick()}>
                    {entry.text}
                    {entry.hasOwnProperty('url') ? <ExternalLogo style={logoStyle} /> : null}
                </MenuEntry>
            </div>
        )
    }

    function titleItemUx(entry: MenuItem): JSX.Element {
        return (
            <div key={entry.title}>
                <div key="divider" style={{ paddingTop: '15px', paddingBottom: '15px' }}>
                    <MenuDivider />
                </div>
                <MenuTitleEntry
                    key={entry.title}
                    selected={!!entry.onClick}
                    onClick={() => invokeEntry(entry)}>
                    <span key="icon" style={{ paddingRight: '12px' }}>{entry.icon}</span>
                    <TitleText>{entry.title}</TitleText>
                </MenuTitleEntry>
            </div>
        )
    }

    function menuEntriesUx(): JSX.Element[] {
        const entries = props.menuItems;
        let res = [];

        res.push(<div key='top_spacer' style={{ height: '30px' }} />);
        res.push(UserDetailsUx());

        for (let entry of entries) {
            res.push(titleItemUx(entry));

            for (let item of entry.listItems) {
                res.push(menuEntryUx(item));
            }
        }

        res.push(<div key='bottom_spacer' style={{ height: '15px' }} />);

        return res;
    }

    const panelStyle: DeepPartial<IPanelStyles> = {
        main: {
            backgroundColor: primaryTheme.palette.white,
            top: "50px"
        },

        commands: {
            marginTop: "5px",
            ".root-226": {
                marginRight: "5px"
            }
        }
    }

    return (
        <>
            <Panel
                isOpen={props.open}
                isLightDismiss={true}
                onDismiss={props.closeCallback}
                onLightDismissClick={props.closeCallback}
                styles={panelStyle} >
                <CenteredUserTitle>ACCOUNT</CenteredUserTitle>
                <DgHorizontalSpacer width={"17px"} />
                <DgHorizontalSpacer width={"290px"}>
                    {menuEntriesUx()}
                </DgHorizontalSpacer>
                <DgHorizontalSpacer width={"23px"} />
            </Panel>
            {orgSwitcher.getOrgSwitcherDialog()}
        </>
    )
}

const OrgNameSwitcherContainer = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: row;
    width: 90%;
    align-items: center;
    cursor: pointer;
    ${props => !!props.disabled && 'cursor: default'};
`;

interface OrganizationNameSwitcherProps {
    orgSwitcherProps: IOrgSwitcher;
}

export const OrganizationNameSwitcher = (props: OrganizationNameSwitcherProps): JSX.Element => {
    const dgIcons = useDgIcons();
    const { switchOrgDisabled, switchOrg } = props.orgSwitcherProps;
    const orgName = useSelector((state: AppState) => state.organizations?.currentOrganization?.name.toLocaleUpperCase());
    const switcherIcon = dgIcons.icon(dgIconNames.Switcher, '', 13, 13, primaryTheme.palette.black, '');

    return (
        <OrgNameSwitcherContainer disabled={switchOrgDisabled} onClick={!switchOrgDisabled ? switchOrg : undefined} title={orgName}>
            <SmallText>@ {orgName}</SmallText>
            {!switchOrgDisabled && <div title="Switch Workspace">{switcherIcon}</div>}
        </OrgNameSwitcherContainer>
    );
}
